import type { NotificationParams, UiLocale } from './i18n';

export type Role = 'admin' | 'contractor';

export const ASSIGNMENT_STATUSES = [
  'Assigned',
  'InProgress',
  'Submitted',
  'ChangesRequested',
  'Approved',
] as const;
export type AssignmentStatus = (typeof ASSIGNMENT_STATUSES)[number];

export const ASSET_KINDS = ['package', 'video', 'sfx', 'voice', 'script', 'other'] as const;
/** What dubbers can see and download. The package already contains the video, voice lines and SFX. */
export const CONTRACTOR_ASSET_KINDS: readonly AssetKind[] = ['package', 'script', 'other'];
/** Uploading or replacing one of these rebuilds the dubbing package. */
export const PACKAGE_INPUT_KINDS: readonly AssetKind[] = ['video', 'voice', 'sfx'];
export type AssetKind = (typeof ASSET_KINDS)[number];

export const ASSET_KIND_LABELS: Record<AssetKind, string> = {
  sfx: 'SFX / music track',
  voice: 'Original voice track',
  script: 'Script',
  other: 'Other',
  video: 'Video (720p)',
  package: 'Dubbing package (.mp4)',
};

/** Whether notification emails are reaching this person. */
export type EmailStatus = 'unknown' | 'ok' | 'failing';

export interface Me {
  discordId: string;
  role: Role;
  displayName: string;
  avatarUrl: string | null;
  /** Interface language; null until the user has picked one on their first visit. */
  locale: UiLocale | null;
  /** Where notifications are emailed; null if unknown. */
  email: string | null;
  /** 'discord' while it's the address from their Discord account; 'custom' once they've changed it. */
  emailSource: 'discord' | 'custom' | null;
}

export interface Contractor {
  discordId: string;
  discordUsername: string;
  displayName: string;
  avatarUrl: string | null;
  languages: string[];
  /** Default rate in minor currency units (cents). */
  defaultRateCents: number;
  currency: string;
  active: boolean;
  emailStatus: EmailStatus;
  emailError?: string;
  /** Joined from their settings for the admin's contractor list. */
  email?: string | null;
  createdAt: string;
}

export interface Project {
  id: string;
  title: string;
  youtubeUrl: string;
  youtubeId: string;
  notes: string;
  notesLocale?: UiLocale;
  /** The 720p video, made from the uploaded file (e.g. a YouTube Studio download). */
  video?: ProjectVideo;
  /** The .mp4 dubbers download: video + voice lines (track 1) + SFX (track 2). */
  package?: ProjectPackage;
  archived: boolean;
  createdAt: string;
}

export interface Asset {
  id: string;
  projectId: string;
  kind: AssetKind;
  filename: string;
  size: number;
  contentType: string;
  /** Audio length measured in the uploader's browser, when it could be read. */
  durationSeconds?: number;
  uploadedAt: string;
}

export interface Assignment {
  id: string;
  projectId: string;
  contractorId: string;
  language: string;
  status: AssignmentStatus;
  rateCents: number;
  currency: string;
  paid: boolean;
  paidAt: string | null;
  latestVersion: number;
  /** Assigned before the dubbing package existed: hidden from the dubber, who is notified once it's ready. */
  awaitingPackage?: boolean;
  createdAt: string;
  updatedAt: string;
}

export type ReviewStatus = 'Pending' | 'Approved' | 'ChangesRequested';

/** A version's voice track mixed with the project's SFX track (admin only). */
export interface MixInfo {
  requestId: string;
  status: 'processing' | 'ready' | 'failed';
  voiceGainDb: number;
  sfxGainDb: number;
  normalize: boolean;
  requestedAt: string;
  finishedAt?: string;
  /** Integrated loudness of the finished mix. */
  loudnessLufs?: number;
  error?: string;
}

/** Sent from the API to the mixer Lambda. */
export interface MixJob {
  requestId: string;
  assignmentId: string;
  version: number;
  sfxKey: string;
  voiceKey: string;
  wavKey: string;
  mp3Key: string;
  voiceGainDb: number;
  sfxGainDb: number;
  normalize: boolean;
}

export interface Submission {
  assignmentId: string;
  version: number;
  filename: string;
  size: number;
  contentType: string;
  durationSeconds?: number;
  note: string;
  /** Interface language of the contractor who wrote the note, for translating it. */
  noteLocale?: UiLocale;
  uploadedAt: string;
  reviewStatus: ReviewStatus;
  reviewNote: string;
  reviewNoteLocale?: UiLocale;
  reviewedAt: string | null;
  /** Only included for the admin. */
  mix?: MixInfo;
}

export type NotificationType =
  | 'assignment_created'
  | 'submission_created'
  | 'changes_requested'
  | 'approved'
  | 'test';

export interface AppNotification {
  id: string;
  userId: string;
  type: NotificationType;
  /** Values for rendering the message in the reader's current language. */
  params?: NotificationParams;
  /** The message as rendered when it was sent (fallback for older notifications). */
  message: string;
  link: string;
  read: boolean;
  createdAt: string;
}

/** Small views of related records, joined onto assignments for display. */
export interface ProjectRef {
  id: string;
  title: string;
  youtubeId: string;
}

export interface ContractorRef {
  discordId: string;
  displayName: string;
  avatarUrl: string | null;
}

export interface AssignmentView extends Assignment {
  project: ProjectRef;
  contractor: ContractorRef;
}

export interface AssignmentDetail extends AssignmentView {
  submissions: Submission[];
  assets: Asset[];
  youtubeUrl: string;
  projectNotes: string;
  projectNotesLocale?: UiLocale;
  /** Length of the project's SFX track, for checking an upload's length before it starts. */
  sfxDurationSeconds?: number;
}

export interface ProjectSummary extends Project {
  languages: string[];
  assignmentCount: number;
  approvedCount: number;
}

export interface NotificationsResponse {
  items: AppNotification[];
  unreadCount: number;
}

export interface ProjectDetail extends Project {
  assets: Asset[];
  assignments: AssignmentView[];
}

export interface StatusBoard {
  languages: string[];
  rows: {
    project: ProjectRef;
    cells: Record<string, AssignmentView[]>;
  }[];
}

export interface DiscordMember {
  discordId: string;
  username: string;
  displayName: string;
  avatarUrl: string | null;
}

export interface InitiateUploadResponse {
  uploadId: string;
  partSize: number;
  partCount: number;
}

export interface SignPartsResponse {
  urls: { partNumber: number; url: string }[];
}

export type CompleteUploadResponse =
  | { purpose: 'asset'; asset: Asset }
  | { purpose: 'submission'; submission: Submission }
  /** A reference video upload: a 720p copy is made in the background. */
  | { purpose: 'video'; processing: true };

export interface DownloadResponse {
  url: string;
}

/** Message placed on the notification queue for the email notifier. */
export interface EmailMessage {
  recipientDiscordId: string;
  to: string;
  subject: string;
  text: string;
  html: string;
}

/** Stable error codes the API returns so the frontend can show errors in the user's language. */
export type ErrorCode =
  | 'not_signed_in'
  | 'access_off'
  | 'admins_only'
  | 'not_found'
  | 'invalid_request'
  | 'conflict'
  | 'already_approved'
  | 'not_audio'
  | 'upload_incomplete'
  | 'discord_unreachable'
  | 'server_error';

export interface ApiErrorBody {
  /** English description, for logs and as a fallback. */
  error: string;
  code: ErrorCode;
}

export interface TranslateResponse {
  translations: string[];
}

export interface ProjectVideo {
  requestId: string;
  /** processing = repackaging or compressing the uploaded file. */
  status: 'processing' | 'ready' | 'failed';
  error?: string;
  updatedAt: string;
}

export interface ProjectPackage {
  requestId: string;
  /** waiting = one of the video, voice lines or SFX is still missing. */
  status: 'waiting' | 'building' | 'ready' | 'failed';
  missing?: AssetKind[];
  error?: string;
  updatedAt: string;
}

/** Sent from the API to the video Lambda. */
export type VideoJob =
  | { requestId: string; projectId: string; mode: 'transcode'; sourceKey: string; title: string }
  | { requestId: string; projectId: string; mode: 'package' };
